<template>
  <div class="banner-form-container">
    <!-- 现代化页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <el-button @click="$router.go(-1)" size="medium" class="back-btn">
            <i class="el-icon-arrow-left"></i> 返回
          </el-button>
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-picture"></i>
              {{ isEdit ? '编辑轮播图' : '新增轮播图' }}
            </h1>
            <p class="page-subtitle">{{ isEdit ? '修改轮播图信息和设置' : '创建新的轮播图展示' }}</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button @click="$router.go(-1)" class="action-btn">
            <i class="el-icon-close"></i> 取消
          </el-button>
          <el-button 
            type="primary" 
            @click="handleSubmit" 
            :loading="saving"
            class="action-btn"
          >
            <i class="el-icon-check"></i> {{ isEdit ? '保存修改' : '创建轮播图' }}
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <el-form 
        :model="bannerForm" 
        :rules="formRules" 
        ref="bannerForm" 
        label-width="120px"
        v-loading="loading"
      >
        <el-row :gutter="30">
          <!-- 左侧：基本信息 -->
          <el-col :span="16">
            <el-card class="form-card">
              <div slot="header" class="card-header">
                <span class="card-title">
                  <i class="el-icon-edit"></i> 基本信息
                </span>
              </div>

              <el-form-item label="轮播图标题" prop="title">
                <el-input 
                  v-model="bannerForm.title" 
                  placeholder="请输入轮播图标题" 
                  size="medium"
                  maxlength="100"
                  show-word-limit
                />
              </el-form-item>

              <el-form-item label="轮播图图片" prop="image_url">
                <div class="image-upload-section">
                  <el-upload
                    :action="uploadAction"
                    :headers="uploadHeaders"
                    :data="uploadData"
                    :on-success="handleImageSuccess"
                    :on-error="handleImageError"
                    :before-upload="beforeImageUpload"
                    :show-file-list="false"
                    class="image-uploader"
                  >
                    <div v-if="bannerForm.image_url" class="image-preview">
                      <img :src="bannerForm.image_url" class="uploaded-image" />
                      <div class="image-overlay">
                        <i class="el-icon-edit-outline"></i>
                        <span>重新上传</span>
                      </div>
                    </div>
                    <div v-else class="image-placeholder">
                      <i class="el-icon-plus"></i>
                      <div class="upload-text">点击上传轮播图</div>
                      <div class="upload-tips">推荐尺寸: 1920x600px，支持JPG、PNG格式</div>
                    </div>
                  </el-upload>
                </div>
              </el-form-item>

              <el-form-item label="排序序号" prop="sort_order">
                <el-input-number 
                  v-model="bannerForm.sort_order" 
                  :min="0" 
                  :max="999"
                  size="medium"
                  placeholder="排序序号"
                />
                <div class="form-tips">数字越小排序越靠前</div>
              </el-form-item>

              <el-form-item label="链接设置">
                <el-radio-group v-model="bannerForm.link_type" @change="handleLinkTypeChange">
                  <el-radio :label="1" border size="medium">无链接</el-radio>
                  <el-radio :label="2" border size="medium">页面跳转</el-radio>
                  <el-radio :label="3" border size="medium">外部链接</el-radio>
                </el-radio-group>
              </el-form-item>

              <el-form-item 
                v-if="bannerForm.link_type !== 1" 
                label="链接地址" 
                prop="link_url"
              >
                <el-input 
                  v-model="bannerForm.link_url" 
                  :placeholder="getLinkPlaceholder()" 
                  size="medium"
                  maxlength="500"
                />
                <div class="form-tips">{{ getLinkTips() }}</div>
              </el-form-item>
            </el-card>

            <!-- 时间设置 -->
            <el-card class="form-card">
              <div slot="header" class="card-header">
                <span class="card-title">
                  <i class="el-icon-time"></i> 时间设置
                </span>
              </div>

              <el-form-item label="显示时间">
                <el-switch
                  v-model="enableTimeRange"
                  active-text="设置时间范围"
                  inactive-text="永久有效"
                  @change="handleTimeRangeChange"
                />
              </el-form-item>

              <template v-if="enableTimeRange">
                <el-form-item label="开始时间" prop="start_time">
                  <el-date-picker
                    v-model="bannerForm.start_time"
                    type="datetime"
                    placeholder="选择开始时间"
                    format="yyyy-MM-dd HH:mm:ss"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    size="medium"
                    style="width: 100%"
                  />
                </el-form-item>

                <el-form-item label="结束时间" prop="end_time">
                  <el-date-picker
                    v-model="bannerForm.end_time"
                    type="datetime"
                    placeholder="选择结束时间"
                    format="yyyy-MM-dd HH:mm:ss"
                    value-format="yyyy-MM-dd HH:mm:ss"
                    size="medium"
                    style="width: 100%"
                  />
                </el-form-item>
              </template>
            </el-card>
          </el-col>

          <!-- 右侧：状态和预览 -->
          <el-col :span="8">
            <el-card class="form-card">
              <div slot="header" class="card-header">
                <span class="card-title">
                  <i class="el-icon-setting"></i> 状态设置
                </span>
              </div>

              <el-form-item label="启用状态">
                <el-switch
                  v-model="bannerForm.is_active"
                  active-text="启用"
                  inactive-text="禁用"
                  active-color="#67C23A"
                  inactive-color="#F56C6C"
                />
                <div class="form-tips">禁用后轮播图将不会在前端显示</div>
              </el-form-item>
            </el-card>

            <!-- 预览卡片 -->
            <el-card class="form-card preview-card" v-if="bannerForm.image_url">
              <div slot="header" class="card-header">
                <span class="card-title">
                  <i class="el-icon-view"></i> 预览效果
                </span>
              </div>

              <div class="banner-preview">
                <div class="preview-image">
                  <img :src="bannerForm.image_url" alt="轮播图预览" />
                  <div class="preview-overlay">
                    <div class="preview-title">{{ bannerForm.title || '轮播图标题' }}</div>
                    <div v-if="bannerForm.link_type !== 1" class="preview-link">
                      <i class="el-icon-link"></i>
                      {{ getLinkTypeText(bannerForm.link_type) }}
                    </div>
                  </div>
                </div>
                <div class="preview-info">
                  <div class="info-item">
                    <span class="info-label">排序:</span>
                    <span class="info-value">{{ bannerForm.sort_order || 0 }}</span>
                  </div>
                  <div class="info-item">
                    <span class="info-label">状态:</span>
                    <el-tag :type="bannerForm.is_active ? 'success' : 'danger'" size="mini">
                      {{ bannerForm.is_active ? '启用' : '禁用' }}
                    </el-tag>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>
      </el-form>
    </div>
  </div>
</template>

<script>
import { getBanner, createBanner, updateBanner } from '@/api/banner'
import { processImageUrl } from '@/utils/config'
import Cookies from 'js-cookie'

export default {
  name: 'BannerForm',
  data() {
    return {
      bannerForm: {
        title: '',
        image_url: '',
        link_type: 1,
        link_url: '',
        sort_order: 0,
        start_time: '',
        end_time: '',
        is_active: true
      },
      
      formRules: {
        title: [
          { required: true, message: '请输入轮播图标题', trigger: 'blur' },
          { min: 2, max: 100, message: '标题长度在 2 到 100 个字符', trigger: 'blur' }
        ],
        image_url: [
          { required: true, message: '请上传轮播图图片', trigger: 'blur' }
        ],
        link_url: [
          { 
            required: true, 
            message: '请输入链接地址', 
            trigger: 'blur',
            validator: this.validateLinkUrl
          }
        ],
        start_time: [
          { validator: this.validateTimeRange, trigger: 'blur' }
        ],
        end_time: [
          { validator: this.validateTimeRange, trigger: 'blur' }
        ]
      },
      
      loading: false,
      saving: false,
      enableTimeRange: false,
      uploadAction: (process.env.VUE_APP_API_BASE_URL || 'https://zdt.sxyqyf.com/api/v1') + '/file/upload/image',
      uploadHeaders: {
        'Authorization': 'Bearer ' + Cookies.get('admin_token')
      },
      uploadData: {
        type: 'other',
        module: 'banner'
      }
    }
  },
  
  computed: {
    isEdit() {
      return !!this.$route.params.id
    },
    
    bannerId() {
      return this.$route.params.id
    }
  },
  
  created() {
    if (this.isEdit) {
      this.fetchBannerData()
    }
  },
  
  methods: {
    async fetchBannerData() {
      this.loading = true
      try {
        const response = await getBanner(this.bannerId)
        const banner = response.data
        
        this.bannerForm = {
          title: banner.title || '',
          image_url: banner.image_url ? processImageUrl(banner.image_url) : '',
          link_type: banner.link_type || 1,
          link_url: banner.link_url || '',
          sort_order: banner.sort_order || 0,
          start_time: banner.start_time || '',
          end_time: banner.end_time || '',
          is_active: banner.is_active !== undefined ? banner.is_active : true
        }
        
        // 检查是否设置了时间范围
        this.enableTimeRange = !!(banner.start_time || banner.end_time)
        
      } catch (error) {
        this.$message.error('获取轮播图信息失败')
        this.$router.go(-1)
      } finally {
        this.loading = false
      }
    },
    
    handleSubmit() {
      this.$refs.bannerForm.validate(async (valid) => {
        if (!valid) {
          this.$message.error('请完善表单信息')
          return
        }
        
        this.saving = true
        try {
          const formData = { ...this.bannerForm }
          
          // 如果没有启用时间范围，清空时间字段
          if (!this.enableTimeRange) {
            formData.start_time = ''
            formData.end_time = ''
          }
          
          if (this.isEdit) {
            await updateBanner(this.bannerId, formData)
            this.$message.success('轮播图更新成功')
          } else {
            await createBanner(formData)
            this.$message.success('轮播图创建成功')
          }
          
          this.$router.push('/admin/banners')
        } catch (error) {
          this.$message.error(this.isEdit ? '更新失败' : '创建失败')
        } finally {
          this.saving = false
        }
      })
    },
    
    handleImageSuccess(response) {
      if (response.code === 200) {
        // 使用processImageUrl处理返回的URL，确保地址正确
        this.bannerForm.image_url = processImageUrl(response.data.url)
        this.$message.success('图片上传成功')
        // 触发表单验证
        this.$refs.bannerForm.validateField('image_url')
      } else {
        this.$message.error(response.message || '图片上传失败')
      }
    },
    
    handleImageError() {
      this.$message.error('图片上传失败，请重试')
    },
    
    beforeImageUpload(file) {
      const isImage = /^image\/(jpeg|jpg|png|gif)$/.test(file.type)
      const isLt5M = file.size / 1024 / 1024 < 5
      
      if (!isImage) {
        this.$message.error('只能上传 JPG、PNG、GIF 格式的图片！')
        return false
      }
      if (!isLt5M) {
        this.$message.error('图片大小不能超过 5MB！')
        return false
      }
      return true
    },
    
    handleLinkTypeChange(value) {
      if (value === 1) {
        this.bannerForm.link_url = ''
      }
    },
    
    handleTimeRangeChange(value) {
      if (!value) {
        this.bannerForm.start_time = ''
        this.bannerForm.end_time = ''
      }
    },
    
    getLinkPlaceholder() {
      if (this.bannerForm.link_type === 2) {
        return '请输入页面路径，如：/pages/activity/detail'
      } else if (this.bannerForm.link_type === 3) {
        return '请输入完整的URL地址，如：https://www.example.com'
      }
      return ''
    },
    
    getLinkTips() {
      if (this.bannerForm.link_type === 2) {
        return '页面跳转：输入小程序内部页面路径'
      } else if (this.bannerForm.link_type === 3) {
        return '外部链接：输入完整的网址链接'
      }
      return ''
    },
    
    getLinkTypeText(type) {
      const typeMap = {
        1: '无链接',
        2: '页面跳转',
        3: '外部链接'
      }
      return typeMap[type] || '未知'
    },
    
    validateLinkUrl(rule, value, callback) {
      if (this.bannerForm.link_type === 1) {
        callback()
        return
      }
      
      if (!value) {
        callback(new Error('请输入链接地址'))
        return
      }
      
      if (this.bannerForm.link_type === 3) {
        const urlPattern = /^https?:\/\/.+/
        if (!urlPattern.test(value)) {
          callback(new Error('外部链接必须以 http:// 或 https:// 开头'))
          return
        }
      }
      
      callback()
    },
    
    validateTimeRange(rule, value, callback) {
      if (!this.enableTimeRange) {
        callback()
        return
      }
      
      const startTime = this.bannerForm.start_time
      const endTime = this.bannerForm.end_time
      
      if (startTime && endTime) {
        if (new Date(startTime) >= new Date(endTime)) {
          callback(new Error('结束时间必须晚于开始时间'))
          return
        }
      }
      
      callback()
    }
  }
}
</script>

<style scoped>
.banner-form-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 页面头部样式 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 20px;
  flex: 1;
}

.back-btn {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  font-weight: 500;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.header-info {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

/* 主要内容区域 */
.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px 40px;
}

/* 表单卡片 */
.form-card {
  margin-bottom: 24px;
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  overflow: hidden;
}

.card-header {
  padding: 0;
}

.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 表单样式 */
.form-tips {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

/* 图片上传样式 */
.image-upload-section {
  width: 100%;
}

.image-uploader {
  width: 100%;
}

.image-preview {
  position: relative;
  width: 400px;
  height: 200px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
}

.image-preview:hover {
  transform: scale(1.02);
}

.uploaded-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.image-preview:hover .image-overlay {
  opacity: 1;
}

.image-overlay i {
  font-size: 24px;
  margin-bottom: 8px;
}

.image-placeholder {
  width: 400px;
  height: 200px;
  border: 2px dashed #d9d9d9;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: #fafafa;
}

.image-placeholder:hover {
  border-color: #409eff;
  background: #f0f9ff;
}

.image-placeholder i {
  font-size: 48px;
  color: #c0c4cc;
  margin-bottom: 12px;
}

.upload-text {
  color: #606266;
  font-size: 16px;
  margin-bottom: 4px;
}

.upload-tips {
  color: #909399;
  font-size: 12px;
}

/* 预览卡片 */
.preview-card {
  position: sticky;
  top: 20px;
}

.banner-preview {
  text-align: center;
}

.preview-image {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;
}

.preview-image img {
  width: 100%;
  height: 120px;
  object-fit: cover;
}

.preview-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.7));
  color: white;
  padding: 16px 12px 12px;
  text-align: left;
}

.preview-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
}

.preview-link {
  font-size: 12px;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 4px;
}

.preview-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.info-label {
  color: #666;
}

.info-value {
  font-weight: 600;
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    padding: 0 20px 20px;
  }
  
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    gap: 15px;
    text-align: center;
  }
  
  .image-preview,
  .image-placeholder {
    width: 100%;
    max-width: 400px;
  }
}
</style> 